import { AxiosInstance } from 'axios';
import { ArgumentValidator } from '@/shared/helpers/argument-validator';
import { QueryStringBuilder } from '../helpers/query-string-builder';
import { ISpeakerQueuesResponse, ISpeakerRequest } from '@/shared/api-client/models/speaker/speaker-request';

export class SpeakerRequestClient {

    constructor(private axiosClient: AxiosInstance) {

    }

    public get apiUrl() {
        return `/speakerRequests`;
    }

    public get speakerQueueExportApiUrl() {
        return `/speakerQueue`;
    }

    public async createOrderQuestion(activityId: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);

        try {
            const response = await this.axiosClient.post(`${this.apiUrl}/${activityId}/orderQuestion`);
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async createAgendaItemSpeech(activityId: string, agendaItemId: string, proposal?: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(agendaItemId, `agendaItemId`);

        try {
            const response = await this.axiosClient.post(`${this.apiUrl}/${activityId}/agendaItemSpeech`, {
                agendaItemId,
                proposal
            });
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async createSpeaker(activityId: string, speakerLabel: string | undefined, agendaItemId?: string, proposal?: string, userId?: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);

        const hasUserId = !!userId && userId.trim().length > 0;
        const hasLabel = !!speakerLabel && speakerLabel.trim().length > 0;

        if (hasUserId === hasLabel) {
            throw new Error(`Exactly one of 'userId' or 'speakerLabel' must be provided`);
        }

        if (!agendaItemId && proposal) {
            throw new Error(`'Proposal' is only allowed when 'agendaItemId' is provided`);
        }

        try {

            const queryString = new QueryStringBuilder()
                .withQueryIfNotEmpty('agendaItemId', agendaItemId)
                .build(`${this.apiUrl}/${activityId}`);

            const response = await this.axiosClient.post(queryString, {
                speakerLabel: hasLabel ? speakerLabel : undefined,
                userId: hasUserId ? userId : undefined,
                proposal
            });

            return response.data;

        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async updateSpeaker(activityId: string, id: string, speakerLabel: string | undefined, proposal?: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {

            const response = await this.axiosClient.put(`${this.apiUrl}/${activityId}/${id}`, {
                speakerLabel: speakerLabel || undefined,
                proposal
            });

            return response.data;

        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async next(activityId: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);

        try {
            const response = await this.axiosClient.post(`${this.apiUrl}/${activityId}/next`);
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async done(activityId: string, id: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {
            const response = await this.axiosClient.post(`${this.apiUrl}/${activityId}/${id}/done`);
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async cancel(activityId: string, id: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {
            const response = await this.axiosClient.post(`${this.apiUrl}/${activityId}/${id}/cancel`, {});
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async delete(activityId: string, id: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {
            const response = await this.axiosClient.delete(`${this.apiUrl}/${activityId}/${id}`);
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async move(activityId: string, id: string, beforeId?: string, afterId?: string): Promise<ISpeakerRequest> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(id, `id`);

        if (beforeId && afterId) {
            throw new Error(`Only one of beforeId or afterId may be provided`);
        }

        try {
            const response = await this.axiosClient.post(`${this.apiUrl}/${activityId}/${id}/move`, {
                beforeId,
                afterId
            });
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async getQueue(activityId: string, agendaItemId: string, staleId: string|undefined = undefined, includeOrderQuestions = false): Promise<ISpeakerQueuesResponse> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(agendaItemId, `agendaItemId`);

        try {
            const url = new QueryStringBuilder()
                .withQueryIfNotEmpty('staleId', staleId)
                .withQueryIfNotEmpty('includeOrderQuestions', includeOrderQuestions)
                .build(`${this.apiUrl}/${activityId}/queue/${agendaItemId}`);

            const response = await this.axiosClient.get(url);
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public async getOrderQuestionQueue(activityId: string, staleId: string|undefined = undefined): Promise<ISpeakerQueuesResponse> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);

        try {
            const url = new QueryStringBuilder()
                .withQueryIfNotEmpty('staleId', staleId)
                .build(`${this.apiUrl}/${activityId}/queue`);

            const response = await this.axiosClient.get(url);
            return response.data;
        } catch (error: any) {
            throw error.response.data;
        }
    }

    public getExportSpeakerQueueLink(baseUrl: string, activityId: string, agendaItemId: string): string {

        ArgumentValidator.notNullOrEmpty(baseUrl, `baseUrl`);
        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(agendaItemId, `agendaItemId`);

        return `${baseUrl}${this.speakerQueueExportApiUrl}/${activityId}/${agendaItemId}/export?fileFormat=Xlsx`;
    }
}
